import mermaidFile from "mermaid/dist/mermaid.min.js?url";

// Runs in a sandboxed frame. Serializing as XML makes the result valid for an <img>, where HTML's <br> would not be.
const RENDER = `
async function render({ id, code, dark }) {
  try {
    mermaid.initialize({ startOnLoad: false, securityLevel: "strict", htmlLabels: false, flowchart: { htmlLabels: false }, theme: dark ? "dark" : "default" });
    const { svg } = await mermaid.render("m" + id, code);
    const root = new DOMParser().parseFromString(svg, "text/html").querySelector("svg");
    const [, , width, height] = root.getAttribute("viewBox").split(" ");
    root.setAttribute("width", width); // the size it was drawn at, rather than the width of whatever holds it
    root.setAttribute("height", height);
    root.style.maxWidth = "";
    parent.postMessage({ id, svg: new XMLSerializer().serializeToString(root) }, "*");
  } catch {
    document.getElementById("dm" + id)?.remove();
    parent.postMessage({ id, failed: true }, "*");
  }
}
let queue = Promise.resolve();
addEventListener("message", (e) => {
  queue = queue.then(() => render(e.data)); // one at a time: a diagram's settings apply to the whole library
});
parent.postMessage({ ready: true }, "*");
`;
const CSP = "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; font-src data:; form-action 'none'";
const TIME_LIMIT = 30_000;

let frame: Promise<Window> | null = null;
let nextId = 0;
const waiting = new Map<number, { resolve: (svg: string) => void; reject: () => void }>();
const finished = new Map<string, Promise<string>>();

/** One hidden frame does all the diagrams, as Mermaid is large. It holds the library itself: a sandboxed page may not be allowed to fetch it. */
function renderer(): Promise<Window> {
  frame ??= fetch(mermaidFile)
    .then((r) => r.text())
    .then(
      (source) =>
        new Promise<Window>((resolve) => {
          const el = document.createElement("iframe");
          el.sandbox.add("allow-scripts"); // no allow-same-origin: it cannot reach this app's cookies or API
          el.setAttribute("aria-hidden", "true");
          el.tabIndex = -1;
          el.style.cssText = "position:fixed;left:-10000px;top:0;width:1000px;height:800px;border:0"; // Mermaid measures text, so it needs layout
          el.srcdoc = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${CSP}"></head><body><script>${source.replace(/<\/script/gi, "<\\/script")}</script><script>${RENDER}</script></body></html>`;
          window.addEventListener("message", (e) => {
            if (e.source !== el.contentWindow) return;
            if (e.data?.ready) return resolve(el.contentWindow!);
            const job = waiting.get(e.data?.id);
            if (!job) return;
            if (typeof e.data.svg === "string") job.resolve(e.data.svg);
            else job.reject();
          });
          document.body.append(el);
        }),
    );
  return frame;
}

/** The SVG for a diagram. Rejects if the diagram has an error. Model output: show the result only in an <img> or a sandbox. */
export function renderMermaid(code: string, dark: boolean): Promise<string> {
  const key = `${dark}:${code}`;
  let result = finished.get(key);
  if (!result) {
    // Mermaid measures edges wrongly while the page is not being drawn (say, a reply that ends in a background tab), so it waits for a frame.
    result = Promise.all([renderer(), new Promise(requestAnimationFrame)]).then(
      ([win]) =>
        new Promise<string>((resolve, reject) => {
          const id = nextId++;
          const timer = setTimeout(reject, TIME_LIMIT);
          const done = () => {
            clearTimeout(timer);
            waiting.delete(id);
          };
          waiting.set(id, {
            resolve: (svg) => {
              done();
              resolve(svg);
            },
            reject: () => {
              done();
              reject(new Error("Diagram error"));
            },
          });
          win.postMessage({ id, code, dark }, "*");
        }),
    );
    finished.set(key, result);
  }
  return result;
}
